import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FaSearch,
  FaArrowRight,
  FaRegFolderOpen,
  FaBolt,
  FaLayerGroup,
  FaEnvelope,
  FaGithub,
  FaLinkedin,
  FaArrowDown,
  FaRegCopy,
  FaCircleNotch,
  FaGlobe,
} from "react-icons/fa";
import { projects as englishProjects, projectMatchesTech } from "../data/projects";
import { architectureFor } from "../data/architectures";
import { useSite, scrollToSection } from "../SiteContext";
import { useTheme } from "../ThemeContext";
import { useI18n, useT } from "../i18n";
import { useContent } from "../i18n/useContent";
import "./CommandPalette.css";

const isMac = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

// `command.group` is an identity key as well as a rendered heading — it is
// compared against literals (the resting-state filter below) and used as a
// Map key to bucket results. It must stay a stable, untranslated id so those
// comparisons keep matching once the language changes; the translated text
// shown to the reader is resolved separately, from this table, at render
// time.
// English title/type/description for each project id, so a search in a
// translated language still finds a project by its English text — the same
// fallback `searchCorpus` in src/data/projects.js gives the grid. Built once
// from the untranslated data import, never from useContent()'s merged
// records (which would just be the current language again).
const ENGLISH_PROJECT_BY_ID = new Map(englishProjects.map((project) => [project.id, project]));

const GROUP_LABEL_KEYS = {
  goto: "palette.groupGoTo",
  projects: "palette.groupProjects",
  automation: "nav.automation",
  tech: "palette.groupTech",
  actions: "palette.groupActions",
  appearance: "theme.appearance",
  language: "language.label",
};

// Subsequence match: "rnat" finds "React Native". Cheap, no dependency, and it
// behaves the way people expect an editor's quick-open to behave.
const fuzzyScore = (haystack, needle) => {
  if (!needle) return 0;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();

  const direct = h.indexOf(n);
  if (direct !== -1) return 1000 - direct;

  let score = 0;
  let hi = 0;
  let streak = 0;
  for (let ni = 0; ni < n.length; ni += 1) {
    const found = h.indexOf(n[ni], hi);
    if (found === -1) return -1;
    streak = found === hi ? streak + 1 : 0;
    score += 10 + streak * 5 - Math.min(found - hi, 10);
    hi = found + 1;
  }
  return score;
};

const CommandPalette = ({ open, onClose }) => {
  const { openProject, filterByTech, clearFilters } = useSite();
  const { setTheme } = useTheme();
  const { languages, setLanguage } = useI18n();
  const t = useT();
  // Projects, workflows and themes come from useContent() rather than the raw
  // data imports, so a translated page shows translated palette entries
  // instead of the English source falling through underneath it.
  const { projects, workflows, skillCategories, themes } = useContent();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const copyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText("hervetusse@gmail.com");
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      window.location.href = "mailto:hervetusse@gmail.com";
    }
  }, []);

  // Every addressable thing on the site, flattened into one list.
  const commands = useMemo(() => {
    const go = (id) => () => scrollToSection(id);

    const sections = [
      { id: "go-home", group: "goto", label: t("nav.home"), icon: <FaArrowRight />, run: go("home") },
      { id: "go-work", group: "goto", label: t("nav.work"), icon: <FaArrowRight />, run: go("projects") },
      {
        id: "go-workflows",
        group: "goto",
        label: t("nav.automation"),
        icon: <FaArrowRight />,
        run: go("workflows"),
      },
      { id: "go-skills", group: "goto", label: t("nav.skills"), icon: <FaArrowRight />, run: go("skills") },
      {
        id: "go-contact",
        group: "goto",
        label: t("footer.contactLabel"),
        icon: <FaArrowRight />,
        run: go("contact"),
      },
    ];

    const projectItems = projects.map((project) => {
      const english = ENGLISH_PROJECT_BY_ID.get(project.id);
      // `english !== project` mirrors the identity check in searchCorpus():
      // when the reader is on English, `english` and `project` are the same
      // object, so appending its own text again would be a no-op that just
      // wastes cycles rather than doubling the corpus.
      const englishKeywords =
        english && english !== project
          ? `${english.title} ${english.type} ${english.description}`
          : "";

      return {
        id: `project-${project.title}`,
        group: "projects",
        label: project.title,
        hint: project.type,
        keywords: [project.technologies.join(" "), englishKeywords].filter(Boolean).join(" "),
        icon: <FaRegFolderOpen />,
        run: () => openProject(project),
      };
    });

    const architectureCommands = projects
      .filter((project) => architectureFor(project.id))
      .map((project) => ({
        id: `architecture-${project.id}`,
        group: "projects",
        label: `${t("projects.architecture")} — ${project.title}`,
        hint: t("palette.hintDiagram"),
        icon: <FaArrowRight />,
        run: () => openProject(project, "arch"),
      }));

    const workflowItems = workflows.map((workflow) => ({
      id: `workflow-${workflow.id}`,
      group: "automation",
      label: workflow.name,
      hint: workflow.cadence,
      keywords: workflow.stack.join(" "),
      icon: <FaBolt />,
      run: go("workflows"),
    }));

    // De-duplicated stack names, each one a filter.
    const seen = new Set();
    const techItems = [];
    [
      ...projects.flatMap((p) => p.technologies),
      ...skillCategories.flatMap((c) => c.skills.map((s) => s.name)),
    ].forEach((tech) => {
      const key = tech.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const count = projects.filter((p) => projectMatchesTech(p, tech)).length;
      if (count === 0) return;
      techItems.push({
        id: `tech-${key}`,
        group: "tech",
        label: tech,
        // Reuses the same pluralized key the projects grid's result count
        // uses — "{display} project(s)" — rather than a hand-rolled plural.
        hint: t("projects.resultCount", { count, display: count }),
        icon: <FaLayerGroup />,
        run: () => filterByTech(tech),
      });
    });

    const actions = [
      {
        id: "action-cv",
        group: "actions",
        label: t("hero.downloadCv"),
        icon: <FaArrowDown />,
        run: () => {
          const a = document.createElement("a");
          a.href = t("resume.file");
          a.download = "";
          a.click();
        },
      },
      {
        id: "action-copy",
        group: "actions",
        label: t("a11y.copyEmail"),
        hint: "hervetusse@gmail.com",
        icon: <FaRegCopy />,
        run: copyEmail,
        keepOpen: true,
      },
      {
        id: "action-email",
        group: "actions",
        label: t("palette.sendEmail"),
        icon: <FaEnvelope />,
        run: () => {
          window.location.href = "mailto:hervetusse@gmail.com";
        },
      },
      {
        id: "action-linkedin",
        group: "actions",
        label: t("palette.openLinkedIn"),
        icon: <FaLinkedin />,
        run: () =>
          window.open(
            "https://www.linkedin.com/in/herve-tusse-8a2479177/",
            "_blank",
            "noopener"
          ),
      },
      {
        id: "action-github",
        group: "actions",
        label: t("palette.openGitHub"),
        icon: <FaGithub />,
        run: () => window.open("https://github.com/htusse", "_blank", "noopener"),
      },
      {
        id: "action-clear",
        group: "actions",
        label: t("palette.clearFilters"),
        icon: <FaLayerGroup />,
        run: clearFilters,
      },
    ];

    // One command per visual mode. Grouped under Appearance (not "goto" or
    // "actions") so they only surface on a query and never crowd the resting
    // shortlist.
    const themeItems = themes.map((theme) => ({
      id: `theme-${theme.id}`,
      group: "appearance",
      label: t("a11y.theme", { label: theme.label }),
      hint: theme.hint,
      keywords: `theme appearance colour color mode ${theme.id}`,
      icon: <FaCircleNotch />,
      run: () => setTheme(theme.id),
    }));

    // One command per language, same "only on a query" treatment as themes.
    // Searchable on native name, English name and code, so "fr", "French"
    // and "Français" all find the same row.
    const languageItems = languages.map((language) => ({
      id: `language-${language.code}`,
      group: "language",
      label: language.nativeName,
      hint: language.englishName,
      keywords: `${language.nativeName} ${language.englishName} ${language.code}`,
      icon: <FaGlobe />,
      run: () => setLanguage(language.code),
    }));

    return [
      ...sections,
      ...projectItems,
      ...architectureCommands,
      ...workflowItems,
      ...techItems,
      ...themeItems,
      ...languageItems,
      ...actions,
    ];
  }, [
    openProject,
    filterByTech,
    clearFilters,
    copyEmail,
    setTheme,
    themes,
    languages,
    setLanguage,
    t,
    projects,
    workflows,
    skillCategories,
  ]);

  const results = useMemo(() => {
    if (!query.trim()) {
      // Resting state: a useful shortlist rather than all 90-odd commands.
      // Compared against the stable group id, not the translated heading —
      // see the GROUP_LABEL_KEYS comment above.
      return commands.filter((c) => c.group === "goto" || c.group === "actions");
    }

    const scored = commands
      .map((command) => {
        const haystack = `${command.label} ${command.hint || ""} ${command.keywords || ""}`;
        const score = Math.max(
          fuzzyScore(command.label, query) * 2,
          fuzzyScore(haystack, query)
        );
        return { command, score };
      })
      .filter((entry) => entry.score > 0);

    // Subsequence matching is generous — the letters of "secret" appear in
    // order inside plenty of unrelated titles. When the query is a real
    // substring of something, only show those; fall back to fuzzy otherwise.
    const direct = scored.filter((entry) => entry.score >= 1000);
    const pool = direct.length > 0 ? direct : scored;

    return pool
      .sort((a, b) => b.score - a.score)
      .slice(0, 24)
      .map((entry) => entry.command);
  }, [commands, query]);

  // Results are rendered under group headings, but arrow keys move through the
  // flat list, so the two orders have to agree. Bucketing happens on the
  // stable group id; the heading text is resolved from GROUP_LABEL_KEYS here,
  // at render time, so it follows the language without disturbing the id.
  const groups = useMemo(() => {
    const order = [];
    const byGroup = new Map();
    results.forEach((command, index) => {
      if (!byGroup.has(command.group)) {
        byGroup.set(command.group, []);
        order.push(command.group);
      }
      byGroup.get(command.group).push({ command, index });
    });
    return order.map((id) => ({
      id,
      name: t(GROUP_LABEL_KEYS[id] || id),
      items: byGroup.get(id),
    }));
  }, [results, t]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // Wait for the element to exist before focusing it.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, results]);

  const runCommand = useCallback(
    (command) => {
      if (!command) return;
      command.run();
      if (!command.keepOpen) onClose();
    },
    [onClose]
  );

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runCommand(results[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="palette-overlay" onMouseDown={onClose}>
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label={t("a11y.palette")}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="palette-input-row">
          <FaSearch className="palette-input-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            className="palette-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("palette.placeholder")}
            aria-label={t("a11y.paletteInput")}
            autoComplete="off"
            spellCheck="false"
          />
          <kbd className="palette-esc">Esc</kbd>
        </div>

        <div className="palette-results" ref={listRef}>
          {results.length === 0 && (
            <p className="palette-empty">{t("palette.empty", { query })}</p>
          )}

          {groups.map((group) => (
            <div className="palette-group" key={group.id}>
              <p className="palette-group-name">{group.name}</p>
              {group.items.map(({ command, index }) => (
                <button
                  key={command.id}
                  type="button"
                  className="palette-item"
                  data-active={index === activeIndex}
                  onMouseMove={() => setActiveIndex(index)}
                  onClick={() => runCommand(command)}
                >
                  <span className="palette-item-icon" aria-hidden="true">
                    {command.icon}
                  </span>
                  <span className="palette-item-label">{command.label}</span>
                  {command.hint && <span className="palette-item-hint">{command.hint}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="palette-footer">
          <span className="palette-legend">
            <kbd>↑</kbd>
            <kbd>↓</kbd> {t("palette.navigate")}
          </span>
          <span className="palette-legend">
            <kbd>↵</kbd> {t("palette.select")}
          </span>
          <span className="palette-legend">
            <kbd>{isMac() ? "⌘" : "Ctrl"}</kbd>
            <kbd>K</kbd> {t("palette.toggle")}
          </span>
          {copied && <span className="palette-copied">{t("palette.copied")}</span>}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CommandPalette;
